import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { trackEvent } from "@/utils/analytics";
import type { World } from "./crewData";
import { quoted, SK_WORLDS, type WorldsContent } from "./worldsContent";
import "./worlds.css";

function useRotatingIndex(length: number, offsetMs: number) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    let timer: number | undefined;
    const start = window.setTimeout(() => {
      timer = window.setInterval(() => setIndex((i) => (i + 1) % length), 3400);
    }, offsetMs);
    return () => {
      window.clearTimeout(start);
      if (timer) window.clearInterval(timer);
    };
  }, [length, offsetMs]);
  return index;
}

function Door({ world, number, offsetMs, content }: { world: World; number: number; offsetMs: number; content: WorldsContent }) {
  const lines = content.doors[world];
  const index = useRotatingIndex(lines.quotes.length, offsetMs);
  const page = content.pages[world];
  const tone = world === "survive" ? "bg-secondary/60 border-border" : "bg-accent/60 border-primary/20";
  return (
    <Link to={page.href} onClick={() => trackEvent("world_door_click", "worlds", world, undefined, { source: "home_doors" })} className={`glass-card hover-lift group flex min-h-[24rem] flex-col gap-5 rounded-xl border p-6 sm:p-8 ${tone}`}>
      <span className="text-xs font-semibold uppercase tracking-wide text-primary">{content.ui.doorLabel} {number}</span>
      <span className="text-2xl font-bold leading-tight text-foreground sm:text-3xl">{page.title}</span>
      <span key={index} className="crew-fade self-start rounded-2xl rounded-bl-sm bg-foreground px-4 py-3 text-lg font-semibold leading-snug text-background">
        {quoted(lines.quotes[index], content.quotes)}
      </span>
      <ul className="space-y-2 text-base text-muted-foreground">
        {lines.points.map((point) => <li key={point}>{point}</li>)}
      </ul>
      <span className="mt-auto inline-flex h-12 items-center gap-2 self-start rounded-lg bg-primary px-6 font-medium text-primary-foreground shadow-xl transition-colors group-hover:bg-primary-hover">
        {content.ui.doorCta}
        <ArrowRight className="h-4 w-4" aria-hidden />
      </span>
    </Link>
  );
}

/** "Where are you today?" — the honest split between the two landing pages. */
export function WorldDoors({ content = SK_WORLDS }: { content?: WorldsContent }) {
  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        <Door world="survive" number={1} offsetMs={0} content={content} />
        <Door world="grow" number={2} offsetMs={1700} content={content} />
      </div>
      <p className="text-center text-muted-foreground">{content.ui.doorsFooter}</p>
    </div>
  );
}
